/**
 * A single chat message.
 *
 * - user  → right-aligned solid bubble, plain text (with attachment chips)
 * - assistant → left-aligned, markdown body, tool-call chips, live streaming
 *   indicator while `status === 'streaming'`
 * - tool  → not rendered on its own (tool activity surfaces via assistant parts)
 */
import type { InboundMedia, InboundMediaKind, MessageDoc, MessagePart } from '@agente/shared'
import { FileText, Image as ImageIcon, Mic, Paperclip, Video } from 'lucide-react'

import { Monogram } from '@/components/brand/logo'
import { ConfirmationCard } from '@/components/chat/confirmation-card'
import { EmailResearchProgress } from '@/components/chat/email-research-progress'
import { MarkdownMessage } from '@/components/chat/markdown-message'
import { SearchSources, webSearchResultData } from '@/components/chat/search-sources'
import { ToolChip } from '@/components/chat/tool-chip'
import { TypingDots } from '@/components/chat/typing-dots'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { formatTime } from '@/lib/format'
import { cn } from '@/lib/utils'

/**
 * A stable React key for a tool part. Parts have no id, so we derive one from
 * the part's type, tool name and a short serialization of its args/result —
 * unique per rendered tool chip without relying on the array index.
 */
function toolPartKey(part: MessagePart): string {
  const payload = part.type === 'toolCall' ? part.toolArgs : part.toolResult
  return `${part.type}:${part.toolName ?? 'tool'}:${JSON.stringify(payload ?? null)}`
}

/**
 * Extrai o jobId do resultado de `startEmailResearch` — o result é o envelope
 * `{ ok: true, data: { jobId, status } }`. Retorna null para qualquer outro
 * part ou formato inesperado.
 */
function emailResearchJobId(part: MessagePart): string | null {
  if (part.type !== 'toolResult' || part.toolName !== 'startEmailResearch') return null
  const result = part.toolResult
  if (!result || typeof result !== 'object') return null
  const data = (result as { data?: unknown }).data
  if (!data || typeof data !== 'object') return null
  const jobId = (data as { jobId?: unknown }).jobId
  return typeof jobId === 'string' ? jobId : null
}

function AttachmentChips({ attachments }: { attachments: NonNullable<MessageDoc['attachments']> }) {
  if (attachments.length === 0) return null
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {attachments.map((att) => (
        <span
          key={att.storagePath}
          className="bg-background/20 flex max-w-full items-center gap-1.5 rounded-md px-2 py-1 text-xs"
        >
          <FileText className="size-3.5 shrink-0" />
          <span className="truncate">{att.name}</span>
        </span>
      ))}
    </div>
  )
}

function mediaIcon(kind: InboundMediaKind) {
  switch (kind) {
    case 'audio':
      return Mic
    case 'image':
      return ImageIcon
    case 'document':
      return FileText
    case 'video':
      return Video
    default:
      return Paperclip
  }
}

/**
 * A stable React key for a media item. Items have no id, so we derive one
 * from a short serialization — unique per rendered chip without relying on
 * the array index (mirrors `toolPartKey` above).
 */
function mediaItemKey(item: InboundMedia): string {
  return `media:${item.kind}:${item.mimeType}:${item.name ?? ''}:${item.sizeBytes ?? ''}:${item.transcript ?? ''}`
}

function mediaKindLabel(kind: InboundMediaKind): string {
  switch (kind) {
    case 'audio':
      return 'Áudio'
    case 'image':
      return 'Imagem'
    case 'document':
      return 'Documento'
    case 'video':
      return 'Vídeo'
    default:
      return 'Anexo'
  }
}

/**
 * Mídias recebidas por um canal externo (WhatsApp) — ver `InboundMedia`. Um
 * áudio cuja transcrição já É o conteúdo da bolha não repete o texto (a
 * transcrição normalmente VIROU `message.content`); imagem/documento/vídeo
 * sempre mostram a descrição curta do modelo quando existir.
 */
function MediaChips({
  media,
  content,
  variant,
}: {
  media: InboundMedia[]
  content: string
  variant: 'user' | 'assistant'
}) {
  if (media.length === 0) return null
  const chipClass = variant === 'user' ? 'bg-background/20' : 'bg-muted text-muted-foreground'
  const noteClass = variant === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'
  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {media.map((item) => {
        const Icon = mediaIcon(item.kind)
        const label = item.kind === 'audio' ? 'Áudio' : (item.name ?? mediaKindLabel(item.kind))
        const transcript = item.transcript?.trim()
        const showTranscript =
          transcript != null &&
          transcript.length > 0 &&
          (item.kind !== 'audio' || transcript !== content.trim())
        return (
          <div key={mediaItemKey(item)} className="flex flex-col gap-1">
            <span
              className={cn(
                'flex w-fit max-w-full items-center gap-1.5 rounded-md px-2 py-1 text-xs',
                chipClass,
              )}
            >
              <Icon className="size-3.5 shrink-0" />
              <span className="truncate">{label}</span>
            </span>
            {showTranscript ? (
              <p className={cn('text-xs italic', noteClass)}>{transcript}</p>
            ) : null}
            {item.skippedReason ? (
              <p className={cn('text-xs', noteClass)}>Anexo não processado: {item.skippedReason}</p>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

export function MessageBubble({
  message,
  conversationId,
}: {
  message: MessageDoc
  /** Conversa que contém a mensagem — necessária para persistir a confirmação. */
  conversationId: string | null
}) {
  if (message.role === 'tool') return null

  const isUser = message.role === 'user'
  const isStreaming = message.status === 'streaming'
  const isError = message.status === 'error'
  const parts = message.parts ?? []
  const toolParts = parts.filter((p) => p.type === 'toolCall' || p.type === 'toolResult')
  // Parts de confirmação (a trava de escritas), com o índice original preservado
  // — o índice é o que o cartão usa para atualizar o part certo no Firestore.
  const confirmationParts = parts
    .map((part, index) => ({ part, index }))
    .filter((entry) => entry.part.type === 'confirmation')
  // Fontes de webSearch: renderizadas assim que os resultados existirem nos
  // parts persistidos (sem layout shift — o bloco só aparece com dados).
  const hasWebSearchSources = parts.some((p) => webSearchResultData(p) !== null)
  const hasBody = message.content.trim().length > 0

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="bg-primary text-primary-foreground max-w-[85%] rounded-2xl rounded-br-sm px-3.5 py-2 sm:max-w-[75%]">
          <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
          {message.attachments ? <AttachmentChips attachments={message.attachments} /> : null}
          {message.media ? (
            <MediaChips media={message.media} content={message.content} variant="user" />
          ) : null}
          <p className="text-primary-foreground/60 mt-1 text-right text-[0.6875rem]">
            {message.channel === 'voice' ? 'por voz · ' : null}
            {message.channel === 'whatsapp' ? 'pelo WhatsApp · ' : null}
            {formatTime(message.createdAt)}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-3">
      <Avatar className="border-border bg-card mt-0.5 size-7 shrink-0 border">
        <AvatarFallback className="bg-card">
          <Monogram size={16} title="Agente Executivo" />
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1 space-y-2">
        {toolParts.length > 0 ? (
          <div className="space-y-1.5">
            {toolParts.map((part) => {
              // O result de startEmailResearch vira um card de progresso ao vivo
              // (assina o job no Firestore) em vez do chip estático, que nunca
              // atualizava. O toolCall do mesmo tool segue como chip normal.
              const jobId = emailResearchJobId(part)
              if (jobId) {
                return <EmailResearchProgress key={`email-research:${jobId}`} jobId={jobId} />
              }
              return <ToolChip key={toolPartKey(part)} part={part} />
            })}
          </div>
        ) : null}

        {isError ? (
          <div className="border-destructive/40 bg-destructive/10 text-destructive rounded-lg border px-3 py-2 text-sm">
            {message.error ?? 'Ocorreu um erro ao gerar a resposta. Tente novamente.'}
          </div>
        ) : hasBody ? (
          <div
            className={cn(
              'bg-card text-card-foreground border-border rounded-2xl rounded-tl-sm border px-3.5 py-2.5',
            )}
          >
            <MarkdownMessage content={message.content} />
            {isStreaming ? (
              <span className="bg-foreground/70 ml-0.5 inline-block h-4 w-0.5 animate-pulse align-middle" />
            ) : null}
          </div>
        ) : isStreaming ? (
          <div className="bg-card border-border w-fit rounded-2xl rounded-tl-sm border px-3.5 py-2">
            <TypingDots />
          </div>
        ) : null}

        {confirmationParts.length > 0 ? (
          <div className="space-y-2">
            {confirmationParts.map(({ part, index }) => (
              <ConfirmationCard
                key={`confirmation:${index}`}
                part={part}
                conversationId={conversationId}
                messageId={message.id}
                partIndex={index}
              />
            ))}
          </div>
        ) : null}

        {message.media ? (
          <MediaChips media={message.media} content={message.content} variant="assistant" />
        ) : null}

        {message.channel === 'voice' || message.channel === 'whatsapp' ? (
          <p className="text-muted-foreground text-[0.6875rem]">
            {message.channel === 'voice' ? 'por voz' : 'pelo WhatsApp'}
          </p>
        ) : null}

        {hasWebSearchSources ? <SearchSources parts={parts} /> : null}
      </div>
    </div>
  )
}
